// Stage 2 "Dashboard" · 2.3 ONE DASHBOARD — the owner-locked look (Mock-up V9 FINAL) as plain tokens, the chip colours and the
// link target of every figure. Every colour is written explicitly (hex), so the page reads the same in the app's dark theme:
// the panels stay the light mock-up colours by design (owner: "keep the light panel colours explicitly").
import { fmtNumber } from "@/lib/format";

export const FD = {
  headerFrom: "#0F4C3A", headerTo: "#17694F", headerText: "#FFFFFF",
  page: "#F6F4EE", cell: "#F6F4EE", panel: "#FFFFFF",
  thBg: "#EFECE4", thText: "#0F4C3A", thLine: "#17694F", rowLine: "#E2DED3",
  text: "#1F2A24", muted: "#6B7280", link: "#0F4C3A",
  green: "#059669", amber: "#D97706", red: "#DC2626", grey: "#9CA3AF",
  // STEP 2 · A6: the app's brand Ivory & Emerald (index.css :root theme, Layout.jsx brand palette) for the header's filled button
  ivory: "#F7F4EC", emerald: "#0A3A2E",
};

export const HEADER_GRADIENT = `linear-gradient(90deg, ${FD.headerFrom} 0%, ${FD.headerTo} 100%)`;

// the five tiles, each its own accent (top border)
export const TILE_ACCENT = { stages: "#4F46E5", assembly: "#7C3AED", ready: "#059669", review: "#D97706", pm: "#DC2626" };

export const STAGES = ["planning", "fieldwork", "completion", "reporting", "locked"];

export const STAGE_CHIP = {
  planning: { bg: "#E0E7FF", fg: "#3730A3" },
  fieldwork: { bg: "#CFFAFE", fg: "#155E75" },
  completion: { bg: "#FEF3C7", fg: "#92400E" },
  reporting: { bg: "#EDE9FE", fg: "#5B21B6" },
  locked: { bg: "#DCFCE7", fg: "#166534" },
};

export const OPINION_CHIP = {
  unmodified: { bg: "#DCFCE7", fg: "#166534" },
  qualified: { bg: "#FEF3C7", fg: "#92400E" },
  adverse: { bg: "#FEE2E2", fg: "#991B1B" },
  disclaimer: { bg: "#FEE2E2", fg: "#991B1B" },
  not_concluded: { bg: "#F3F4F6", fg: "#374151" },
};

export const CURRENCY_BADGE = {
  SAR: { bg: "#DCFCE7", fg: "#166534" },
  USD: { bg: "#DBEAFE", fg: "#1E40AF" },
  other: { bg: "#F3F4F6", fg: "#374151" },
};
export const currencyBadge = (cur) => CURRENCY_BADGE[String(cur || "").toUpperCase()] || CURRENCY_BADGE.other;

export const BAND_COLOR = { green: FD.green, amber: FD.amber, red: FD.red };

// the heat-map cell shade by the strongest rating in the head
export const HEAT_SHADE = {
  significant: { bg: "#7F1D1D", fg: "#FFFFFF" },
  high: { bg: "#DC2626", fg: "#FFFFFF" },
  medium: { bg: "#F59E0B", fg: "#1F2937" },
  low: { bg: "#A7F3D0", fg: "#064E3B" },
  none: { bg: "#F6F4EE", fg: "#9CA3AF" },
};
export function heatLevel(head) {
  if (!head || !head.count) return "none";
  if (head.significant) return "significant";
  if (head.high) return "high";
  // the contract carries count / high / significant per head: a head with risks but none high is shown as medium;
  // "low" is reserved for a head the API marks low (head.low) — see the open point in the report
  if (head.low && !head.medium) return "low";
  return "medium";
}

// Portfolio column widths (percent of the table, fixed layout, text wraps; sum = 100) — SUNDAY BATCH D-1 widened the review-point column
export const PORTFOLIO_COLS = [
  { id: "client", width: 20 }, { id: "status", width: 10 }, { id: "tested", width: 10 }, { id: "signoffs", width: 10 },
  { id: "lock", width: 11 }, { id: "report", width: 14 }, { id: "pm", width: 13 }, { id: "rp", width: 12 },   // D-1: rp = n / n / n
];

// The page of each stage (Portfolio "Status" → the stage's page)
export const STAGE_PAGE = {
  planning: "/planning", fieldwork: "/head-folders", completion: "/completion",
  reporting: "/reporting/auditors-report", locked: "/close/file-assembly",
};

// Every link target of the dashboard in one place (the Engagements tab's `?stage=` is documented for agent D; the Billing
// `?user=` and Audit-trail `?tab=signoffs` params are handled in this change; `/risk?head=` is documented for the risk page)
export const TARGET = {
  engagements: "/engagements",
  engagementsByStage: (stage) => `/engagements?stage=${encodeURIComponent(stage)}`,
  stagePage: (stage) => STAGE_PAGE[stage] || "/engagements",
  vouching: "/vouching",
  signoffs: "/audit-trail?tab=signoffs",
  fileAssembly: "/close/file-assembly",
  auditorsReport: "/reporting/auditors-report",
  w9: "/completion/misstatements",
  reviewPoints: "/review-points",
  billing: "/billing",
  billingUser: (userId) => `/billing?user=${encodeURIComponent(userId)}`,
  aiUsage: "/ai-usage",
  riskHead: (fsCode) => `/risk?head=${encodeURIComponent(fsCode)}`,
  recycleBin: "/recycle-bin",
};

// Old dashboard URLs that redirect to "/" (App.js maps each to <Navigate to="/" replace />)
export const LEGACY_DASHBOARD_PATHS = ["executive", "dashboard", "workspace"];


// SUNDAY BATCH D-6 — the ONE hours formatter of the dashboard (billing, staff time, my time): below 0.1 h the time is shown in
// minutes ("4 min" / "4 دقيقة"), never "0.0"; from 0.1 h on, hours with one decimal. `unit: false` gives the bare number for
// a column headed "hours" (minutes always carry their unit).
export const HOURS_AS_MINUTES_BELOW = 0.1;
export function fmtHours(hours, lang, t, { unit = true } = {}) {
  const v = Number(hours || 0);
  if (Math.abs(v) < HOURS_AS_MINUTES_BELOW) return fill(t("fd.minutes"), { n: fmtNumber(Math.round(v * 60), lang) });
  const s = fmtNumber(v, lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return unit ? fill(t("fd.hours"), { h: s }) : s;
}
export const fmtSeconds = (seconds, lang, t, opts) => fmtHours(Number(seconds || 0) / 3600, lang, t, opts);

// Over-run tone: green actual ≤ budget; red actual > budget (even one minute); grey when no budget is set
// The API (backend/firm_dashboard.py) sends overrun.status "green" | "red" | "no_budget"; the fallback derives it from the hours.
export function overrunTone(row) {
  if (!row || row.budget_set === false) return "grey";
  const st = row.overrun && row.overrun.status;
  if (st === "no_budget") return "grey";
  if (st === "red" || st === "green") return st;
  const b = Number(row.budgeted_hours ?? row.budget_hours ?? 0), a = Number(row.hours_spent ?? row.actual_hours ?? 0);
  return a > b ? "red" : "green";
}

export const fill = (s, vars) => String(s || "").replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
